import "./CompanyPriceLists.css";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { usePermissions } from "./PermissionContext";

const API_URL = "/api";

const emptyPriceList = {
  name: "",
  description: "",
  is_default: false,
  valid_from: "",
  valid_until: "",
};

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem(
      "company_token"
    )}`,
  },
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "Sin límite";

  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "es-CO"
  );
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

function CompanyPriceLists() {
  const { can } = usePermissions();

  const canManageProducts = can("products.manage");

  const [priceLists, setPriceLists] = useState([]);
  const [selectedPriceList, setSelectedPriceList] =
    useState(null);

  const [priceValues, setPriceValues] = useState({});
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] =
    useState(false);

  const [showCreateForm, setShowCreateForm] =
    useState(false);

  const [priceListForm, setPriceListForm] =
    useState(emptyPriceList);

  const [savingPriceList, setSavingPriceList] =
    useState(false);

  const [savingPrices, setSavingPrices] =
    useState(false);

  const loadPriceLists = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/price-lists/me`,
        getHeaders()
      );

      setPriceLists(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      await loadPriceLists();

      setLoading(false);
    };

    loadInitialData();
  }, [loadPriceLists]);

  const loadPriceListDetail = async (priceListId) => {
    setLoadingDetail(true);

    try {
      const response = await axios.get(
        `${API_URL}/price-lists/me/${priceListId}`,
        getHeaders()
      );

      setSelectedPriceList(response.data);

      setPriceValues(
        response.data.items.reduce((result, item) => {
          result[item.product_id] = String(item.price ?? 0);
          return result;
        }, {})
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePriceListFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    setPriceListForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const createPriceList = async (event) => {
    event.preventDefault();

    if (!canManageProducts) {
      toast.error(
        "No tienes permiso para administrar listas de precios."
      );
      return;
    }

    setSavingPriceList(true);

    try {
      const response = await axios.post(
        `${API_URL}/price-lists/me`,
        {
          name: priceListForm.name.trim(),
          description:
            priceListForm.description.trim() || null,
          is_default: priceListForm.is_default,
          valid_from: priceListForm.valid_from || null,
          valid_until: priceListForm.valid_until || null,
        },
        getHeaders()
      );

      setSelectedPriceList(response.data);

      setPriceValues(
        response.data.items.reduce((result, item) => {
          result[item.product_id] = String(item.price ?? 0);
          return result;
        }, {})
      );

      setPriceListForm(emptyPriceList);
      setShowCreateForm(false);

      toast.success(
        "Lista de precios creada correctamente."
      );

      await loadPriceLists();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingPriceList(false);
    }
  };

  const setDefaultPriceList = async (priceList) => {
    if (priceList.is_default) return;

    try {
      await axios.patch(
        `${API_URL}/price-lists/me/${priceList.id}`,
        {
          is_default: true,
        },
        getHeaders()
      );

      toast.success(
        `"${priceList.name}" ahora es la lista predeterminada.`
      );

      await loadPriceLists();

      if (selectedPriceList?.id === priceList.id) {
        await loadPriceListDetail(priceList.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const togglePriceListStatus = async (priceList) => {
    try {
      await axios.patch(
        `${API_URL}/price-lists/me/${priceList.id}`,
        {
          is_active: !priceList.is_active,
        },
        getHeaders()
      );

      toast.success(
        priceList.is_active
          ? "Lista de precios desactivada."
          : "Lista de precios activada."
      );

      await loadPriceLists();

      if (selectedPriceList?.id === priceList.id) {
        await loadPriceListDetail(priceList.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveChangedPrices = async () => {
    if (!selectedPriceList) return;

    const itemsToUpdate = selectedPriceList.items
      .map((item) => ({
        product_id: item.product_id,
        price: Number(priceValues[item.product_id]),
        original_price: Number(item.price),
      }))
      .filter(
        (item) =>
          Number.isFinite(item.price) &&
          item.price >= 0 &&
          item.price !== item.original_price
      )
      .map(({ product_id, price }) => ({
        product_id,
        price,
      }));

    if (itemsToUpdate.length === 0) {
      toast.info("No hay precios modificados para guardar.");
      return;
    }

    setSavingPrices(true);

    try {
      const response = await axios.put(
        `${API_URL}/price-lists/me/${selectedPriceList.id}/items`,
        {
          items: itemsToUpdate,
        },
        getHeaders()
      );

      setSelectedPriceList(response.data);

      setPriceValues(
        response.data.items.reduce((result, item) => {
          result[item.product_id] = String(item.price ?? 0);
          return result;
        }, {})
      );

      toast.success(
        `${itemsToUpdate.length} precio(s) actualizado(s).`
      );

      await loadPriceLists();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingPrices(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!selectedPriceList) return [];

    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return selectedPriceList.items;
    }

    return selectedPriceList.items.filter((item) =>
      `${item.product_name} ${item.product_sku}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [search, selectedPriceList]);

  if (loading) {
    return <h2>Cargando listas de precios...</h2>;
  }

  return (
    <div className="company-price-lists">
      <section className="price-lists-heading">
        <div>
          <p className="price-lists-kicker">
            Catálogo comercial
          </p>

          <h1>Listas de precios</h1>

          <p>
            Define precios para mayoristas, promociones,
            canales de venta o clientes especiales.
          </p>
        </div>

        {canManageProducts && (
          <button
            type="button"
            className="btn-price-list-create"
            onClick={() =>
              setShowCreateForm((current) => !current)
            }
          >
            {showCreateForm
              ? "Cerrar formulario"
              : "Nueva lista"}
          </button>
        )}
      </section>

      {showCreateForm && (
        <section className="price-list-form-card">
          <h2>Crear lista de precios</h2>

          <form onSubmit={createPriceList}>
            <div className="price-list-form-grid">
              <label>
                Nombre
                <input
                  type="text"
                  name="name"
                  value={priceListForm.name}
                  onChange={handlePriceListFormChange}
                  maxLength="120"
                  placeholder="Ejemplo: Mayoristas"
                  required
                />
              </label>

              <label>
                Vigencia desde
                <input
                  type="date"
                  name="valid_from"
                  value={priceListForm.valid_from}
                  onChange={handlePriceListFormChange}
                />
              </label>

              <label>
                Vigencia hasta
                <input
                  type="date"
                  name="valid_until"
                  value={priceListForm.valid_until}
                  onChange={handlePriceListFormChange}
                />
              </label>
            </div>

            <label className="price-list-description">
              Descripción
              <textarea
                name="description"
                rows="2"
                value={priceListForm.description}
                onChange={handlePriceListFormChange}
                placeholder="Uso o público objetivo de esta lista"
              />
            </label>

            <label className="price-list-default-option">
              <input
                type="checkbox"
                name="is_default"
                checked={priceListForm.is_default}
                onChange={handlePriceListFormChange}
              />

              <span>
                <strong>Usar como lista predeterminada</strong>
                <small>
                  Reemplaza la lista predeterminada actual.
                </small>
              </span>
            </label>

            <button
              type="submit"
              className="btn-price-list-save"
              disabled={savingPriceList}
            >
              {savingPriceList
                ? "Creando lista..."
                : "Crear lista y copiar precios base"}
            </button>
          </form>
        </section>
      )}

      <section className="price-list-layout">
        <article className="price-list-panel">
          <div className="price-list-panel-heading">
            <h2>Listas disponibles</h2>
            <span>{priceLists.length}</span>
          </div>

          {priceLists.length === 0 ? (
            <p className="price-list-empty">
              No hay listas de precios configuradas.
            </p>
          ) : (
            <div className="price-list-options">
              {priceLists.map((priceList) => (
                <button
                  type="button"
                  key={priceList.id}
                  className={`price-list-option ${
                    selectedPriceList?.id === priceList.id
                      ? "selected"
                      : ""
                  }`}
                  onClick={() =>
                    loadPriceListDetail(priceList.id)
                  }
                >
                  <span>
                    <strong>{priceList.name}</strong>

                    <small>
                      {priceList.total_products} productos
                    </small>
                  </span>

                  <span className="price-list-option-tags">
                    {priceList.is_default && (
                      <em>Predeterminada</em>
                    )}

                    {!priceList.is_active && (
                      <em className="inactive">Pausada</em>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </article>

        <article className="price-list-detail-panel">
          {loadingDetail ? (
            <p className="price-list-empty">
              Cargando precios...
            </p>
          ) : !selectedPriceList ? (
            <p className="price-list-empty">
              Selecciona una lista para ver y modificar
              sus precios.
            </p>
          ) : (
            <>
              <div className="price-list-detail-heading">
                <div>
                  <p>Lista de precios</p>
                  <h2>{selectedPriceList.name}</h2>

                  <span>
                    Vigencia:{" "}
                    {formatDate(
                      selectedPriceList.valid_from
                    )}{" "}
                    a{" "}
                    {formatDate(
                      selectedPriceList.valid_until
                    )}
                  </span>
                </div>

                {canManageProducts && (
                  <div className="price-list-actions">
                    {!selectedPriceList.is_default && (
                      <button
                        type="button"
                        className="btn-price-list-default"
                        onClick={() =>
                          setDefaultPriceList(
                            selectedPriceList
                          )
                        }
                      >
                        Usar como predeterminada
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn-price-list-toggle"
                      onClick={() =>
                        togglePriceListStatus(
                          selectedPriceList
                        )
                      }
                    >
                      {selectedPriceList.is_active
                        ? "Desactivar"
                        : "Activar"}
                    </button>
                  </div>
                )}
              </div>

              {selectedPriceList.description && (
                <p className="price-list-detail-description">
                  {selectedPriceList.description}
                </p>
              )}

              <input
                type="search"
                className="price-list-search"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Buscar producto o código"
              />

              <div className="price-list-items-wrapper">
                <table className="price-list-items-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Precio base</th>
                      <th>Precio en esta lista</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.product_name}</strong>
                          <small>{item.product_sku}</small>
                        </td>

                        <td>
                          {formatCurrency(
                            item.base_sale_price
                          )}
                        </td>

                        <td>
                          {canManageProducts ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={
                                priceValues[item.product_id] ??
                                ""
                              }
                              onChange={(event) =>
                                setPriceValues((current) => ({
                                  ...current,
                                  [item.product_id]:
                                    event.target.value,
                                }))
                              }
                            />
                          ) : (
                            formatCurrency(item.price)
                          )}
                        </td>
                      </tr>
                    ))}

                    {filteredItems.length === 0 && (
                      <tr>
                        <td colSpan="3">
                          No hay productos que coincidan.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {canManageProducts && (
                <button
                  type="button"
                  className="btn-price-list-save"
                  onClick={saveChangedPrices}
                  disabled={
                    savingPrices ||
                    !selectedPriceList.is_active
                  }
                >
                  {savingPrices
                    ? "Guardando precios..."
                    : "Guardar precios modificados"}
                </button>
              )}
            </>
          )}
        </article>
      </section>
    </div>
  );
}

export default CompanyPriceLists;